import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router';
import { useToasts, type ToastItem } from '@/stores/toasts';
import { useSession } from '@/stores/session';
import { retryNow, useSocketStatus } from '@/socket/socket';
import { useIsMobile } from './useMedia';
import { Button } from './Button';
import { MiscArt } from '@/art/MiscArt';
import type { MiscName } from '@/art/misc';
import s from './feedback.module.css';
import { cx } from './cx';
import { UiIcon } from '@/art/UiIcon';

/* ======================= Dialog ======================= */
export interface DialogProps {
  open: boolean;
  onClose(): void;
  /** accessible name */
  labelledBy?: string;
  label?: string;
  /** desktop width (px) */
  width?: number;
  /** force sheet/modal; default: sheet on phones, modal on desktop */
  mode?: 'auto' | 'modal' | 'sheet';
  children: ReactNode;
  className?: string;
}

/** Desktop: centred modal over a scrim. Phone: bottom sheet with a handle. Esc / scrim tap closes. */
export function Dialog({ open, onClose, labelledBy, label, width = 760, mode = 'auto', children, className }: DialogProps) {
  const mobile = useIsMobile();
  const sheet = mode === 'sheet' || (mode === 'auto' && mobile);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div className={cx(s.scrim, sheet && s.sheetScrim)} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-label={label}
        tabIndex={-1}
        className={cx(sheet ? s.sheet : s.modal, className)}
        style={sheet ? undefined : ({ ['--dlg-w' as string]: `${width}px` } as CSSProperties)}
      >
        {sheet && <span className={s.handle} aria-hidden="true" />}
        {children}
      </div>
    </div>,
    document.body,
  );
}

export function CloseButton({ onClick }: { onClick(): void }) {
  const intl = useIntl();
  return (
    <button type="button" className={s.close} onClick={onClick} aria-label={intl.formatMessage({ id: 'common.close' })}>
      <UiIcon name="close" size={20} fallback="×" />
    </button>
  );
}

/* ======================= ErrorState ======================= */
export type ErrorTone = 'red' | 'orange' | 'blue' | 'teal';

export interface ErrorStateProps {
  /** mono caps context line, e.g. "LOBBY · OFFLINE" */
  context?: ReactNode;
  glyph: string;
  /** illustration (art/misc) instead of the diamond glyph */
  art?: MiscName;
  tone: ErrorTone;
  title: ReactNode;
  text?: ReactNode;
  primary?: { label: ReactNode; onClick(): void };
  secondary?: { label: ReactNode; onClick(): void };
  className?: string;
}

/** Full-panel error / empty state (design/system "06 Errors"): diamond glyph, title, text, 2 actions. */
export function ErrorState({ context, glyph, art, tone, title, text, primary, secondary, className }: ErrorStateProps) {
  return (
    <div className={cx(s.errPanel, className)} style={{ ['--c' as string]: `var(--${tone})` } as CSSProperties} role="alert">
      {context !== undefined && <div className={s.errCtx}>{context}</div>}
      <div className={s.errBody}>
        {art ? (
          <MiscArt name={art} width={240} className={s.errArt} />
        ) : (
          <div className={s.diamond} aria-hidden="true">
            <span>{glyph}</span>
          </div>
        )}
        <h2 className={s.errTitle}>{title}</h2>
        {text !== undefined && <p className={s.errText}>{text}</p>}
        {(primary || secondary) && (
          <div className={s.errActions}>
            {primary && (
              <Button tone={tone} size="md" block onClick={primary.onClick} style={{ minHeight: 50, fontSize: 15, fontWeight: 700 }}>
                {primary.label}
              </Button>
            )}
            {secondary && (
              <Button tone="neutral" variant="outline" block onClick={secondary.onClick} style={{ minHeight: 46, fontWeight: 400 }}>
                {secondary.label}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ======================= Notice ======================= */
/** Inline notice card (e.g. "Round already started — your bet goes into the next round"). */
export function Notice({ tone = 'orange', title, text, className }: { tone?: ErrorTone; title: ReactNode; text?: ReactNode; className?: string }) {
  return (
    <div
      role="status"
      className={cx(s.notice, className)}
      style={{ ['--c' as string]: `var(--${tone})`, ['--tint' as string]: `var(--${tone}-tint)` } as CSSProperties}
    >
      <span className={s.noticeTitle}>{title}</span>
      {text !== undefined && <span className={s.noticeText}>{text}</span>}
    </div>
  );
}

/* ======================= Connection banner ======================= */
/** Red strip "No connection · reconnecting… 3s" — only after the socket was connected once. */
export function ConnectionBanner() {
  const connection = useSession((st) => st.connection);
  const everConnected = useSession((st) => st.everConnected);
  const retryAt = useSocketStatus((st) => st.retryAt);
  const [now, setNow] = useState(() => Date.now());
  const show = everConnected && connection !== 'online';

  useEffect(() => {
    if (!show) return;
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, [show]);

  if (!show) return null;
  const secs = retryAt ? Math.max(0, Math.ceil((retryAt - now) / 1000)) : 0;
  return (
    <div className={s.banner} role="alert">
      <span>
        <FormattedMessage id="common.offline.banner" />
      </span>
      <button type="button" className={cx(s.bannerBtn, 'mono')} onClick={retryNow}>
        {secs > 0 ? `${secs}s` : '…'}
      </button>
    </div>
  );
}

/* ======================= Toaster ======================= */
const TONE: Record<ToastItem['kind'], string> = { error: 'var(--red)', notice: 'var(--orange)', success: 'var(--teal)' };

function ToastView({ t }: { t: ToastItem }) {
  const dismiss = useToasts((st) => st.dismiss);
  const intl = useIntl();
  useEffect(() => {
    const h = setTimeout(() => dismiss(t.id), t.ttl);
    return () => clearTimeout(h);
  }, [t.id, t.ttl, dismiss]);
  return (
    <div className={s.toast} style={{ ['--c' as string]: TONE[t.kind] } as CSSProperties} role={t.kind === 'error' ? 'alert' : 'status'}>
      <div className={s.toastBody}>
        <span className={s.toastTitle}>{intl.formatMessage({ id: t.titleId }, t.values)}</span>
        {t.textId && <span className={s.toastText}>{intl.formatMessage({ id: t.textId }, t.values)}</span>}
        {t.link && (
          <Link to={t.link.to} className={s.toastLink} onClick={() => dismiss(t.id)}>
            {intl.formatMessage({ id: t.link.labelId }, t.values)}
          </Link>
        )}
      </div>
      <button type="button" className={s.toastClose} onClick={() => dismiss(t.id)} aria-label={intl.formatMessage({ id: 'common.close' })}>
        <UiIcon name="close" size={16} fallback="×" />
      </button>
    </div>
  );
}

export function Toaster() {
  const items = useToasts((st) => st.items);
  return (
    <div className={s.toaster} aria-live="polite">
      {items.map((t) => (
        <ToastView key={t.id} t={t} />
      ))}
    </div>
  );
}

export const feedbackClasses = s;
